import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";

const FORM_ADDR = "https://readdy.ai/api/form/da2lca3d3r93mn1k3bd0";

function Contact() {
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // anti-spam honeypot: silently ignore bots
    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setFormState("success");
      form.reset();
      return;
    }

    const payload = new FormData();
    Array.from(fd.entries()).forEach(([k, v]) => {
      if (k === "website_alt") return;
      payload.append(k, v);
    });

    setFormState("submitting");
    setFormError("");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (res.ok && code === "OK") {
        setFormState("success");
        form.reset();
      } else {
        setFormState("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setFormState("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <Layout>
      <section className="relative overflow-hidden bg-foreground-950 py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, oklch(var(--primary-500) / 0.6), transparent 45%), radial-gradient(circle at 80% 70%, oklch(var(--accent-500) / 0.5), transparent 45%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-background-400">
            <Link to="/" className="transition-colors hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-200">联系我们</span>
          </nav>
          <h1 className="mt-6 font-heading text-3xl font-bold text-background-50 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            无论是作品推荐、专题建议，还是使用反馈，都可以通过下方表单告诉我们。我们会在收到留言后尽快处理。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-5 md:px-6">
          <div className="md:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">期待你的来信</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              你的每一个推荐与建议，都能帮助我们持续完善韩漫大全分类专题，为更多读者发现好看的韩国漫画。
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: "ri-mail-send-line", label: "联系邮箱", value: "hello@manhwa-journal.example.com" },
                { icon: "ri-time-line", label: "回复时间", value: "工作日内 1–3 天回复" },
                { icon: "ri-chat-3-line", label: "推荐作品", value: "告诉我们名称与题材即可" },
              ].map((item) => (
                <div key={item.label} className="flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{item.label}</div>
                    <p className="mt-0.5 text-sm text-foreground-600">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              {formState === "success" ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-3xl" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">留言已收到</h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    感谢你的反馈，我们会尽快与你联系。如需再次留言，可重新填写。
                  </p>
                  <button
                    type="button"
                    onClick={() => setFormState("idle")}
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form="联系我们留言表单"
                  className="space-y-5"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        你的称呼
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder="例如：阿岚"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        联系邮箱
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      主题
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="作品推荐">作品推荐</option>
                      <option value="专题建议">专题建议</option>
                      <option value="使用反馈">使用反馈</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="写下你想推荐的作品，或对我们的建议……"
                      className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                    <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formState === "error" && formError && (
                    <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {formState === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                        提交中…
                      </>
                    ) : (
                      <>
                        提交留言
                        <i className="ri-send-plane-line" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Contact;